// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ShopOrderResponse} from '@fluxer/schema/src/domains/shop/ShopResponseSchemas';

type PaddleCheckoutData = NonNullable<ShopOrderResponse['paddle_checkout']>;

export interface PaddleCheckoutEvents {
	onCompleted(): void;
	onClosed(): void;
}

interface PaddleEvent {
	name?: string;
}

interface PaddleJs {
	readonly Initialized: boolean;
	Environment: {set(environment: 'sandbox'): void};
	Initialize(options: {token: string; eventCallback(event: PaddleEvent): void}): void;
	Checkout: {open(options: {transactionId: string; settings: {displayMode: 'overlay'; locale?: string}}): void};
}

// Paddle asks to always load Paddle.js from its CDN (it updates in place, so no integrity hash). Loaded once, on the
// first in-page checkout; never at app start.
const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

// Paddle.Checkout.open settings.locale; another locale leaves Paddle the browser's.
const PADDLE_LOCALES = new Set([
	'ar',
	'zh-Hans',
	'zh-TW',
	'da',
	'nl',
	'en',
	'fr',
	'de',
	'it',
	'ja',
	'ko',
	'no',
	'pl',
	'pt',
	'pt-BR',
	'tr',
	'ru',
	'es',
	'sv',
]);

let loading: Promise<PaddleJs> | null = null;
let listener: PaddleCheckoutEvents | null = null;
// Paddle.js did not load, or Paddle could not open a checkout here: the payment page takes over.
let unavailable = false;

export function paddleCheckoutUnavailable(): boolean {
	return unavailable;
}

export function paddleLocale(locale: string | undefined): string | undefined {
	if (!locale) return undefined;
	if (PADDLE_LOCALES.has(locale)) return locale;
	if (locale === 'zh-CN') return 'zh-Hans';
	const language = locale.split('-')[0]!;
	return PADDLE_LOCALES.has(language) ? language : undefined;
}

function loadPaddleJs(): Promise<PaddleJs> {
	loading ??= new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = PADDLE_JS_URL;
		script.async = true;
		script.addEventListener('load', () => {
			const paddle = (window as {Paddle?: PaddleJs}).Paddle;
			if (paddle) resolve(paddle);
			else reject(new Error('Paddle.js loaded without Paddle'));
		});
		script.addEventListener('error', () => reject(new Error('Paddle.js did not load')));
		document.head.append(script);
	});
	return loading;
}

function dispatch(event: PaddleEvent): void {
	if (event.name === 'checkout.completed') listener?.onCompleted();
	else if (event.name === 'checkout.closed') listener?.onClosed();
	else if (event.name === 'checkout.error') unavailable = true;
}

// Opens the order's Paddle transaction as an overlay on this page. Paddle.js is initialized once per page load, with the
// first order's token and environment; the server only hands out one of each.
export async function openPaddleCheckout(
	checkout: PaddleCheckoutData,
	locale: string | undefined,
	events: PaddleCheckoutEvents,
): Promise<void> {
	try {
		const paddle = await loadPaddleJs();
		if (!paddle.Initialized) {
			if (checkout.environment === 'sandbox') paddle.Environment.set('sandbox');
			paddle.Initialize({token: checkout.client_token, eventCallback: dispatch});
		}
		listener = events;
		paddle.Checkout.open({
			transactionId: checkout.transaction_id,
			settings: {displayMode: 'overlay', ...(locale ? {locale} : {})},
		});
	} catch (error) {
		unavailable = true;
		throw error;
	}
}
